/**
 * Notification Delivery Status Service
 * 
 * Service for fetching notification delivery statistics for admin reporting.
 */

import { supabase } from '@/integrations/supabase/client';

export interface DeliveryStats {
  pending: number;
  processing: number;
  delivered: number;
  failed: number;
  total: number;
}

export interface RecentFailure {
  id: string;
  notification_id: string;
  channel: string;
  recipient_email: string | null;
  attempts: number;
  error_details: Record<string, unknown> | null;
  updated_at: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  byType: Record<string, number>;
}

/**
 * Get delivery queue statistics for the company
 * 
 * Uses server-side aggregation for efficiency:
 * - pending/processing: current queue state
 * - delivered: last 24 hours
 * - failed: last 7 days
 */
export async function getDeliveryStats(companyId: string): Promise<DeliveryStats> {
  const { data, error } = await supabase
    .rpc('get_delivery_queue_stats', { p_company_id: companyId })
    .single();

  if (error) {
    console.error('[DeliveryStats] Error fetching stats via RPC:', error);
    throw error;
  }

  return {
    pending: Number(data?.pending ?? 0),
    processing: Number(data?.processing ?? 0),
    delivered: Number(data?.delivered ?? 0),
    failed: Number(data?.failed ?? 0),
    total: Number(data?.total ?? 0),
  };
}

/**
 * Get recent failed deliveries for admin review
 */
export async function getRecentFailures(companyId: string, limit = 10): Promise<RecentFailure[]> {
  const { data, error } = await supabase
    .from('notification_delivery_queue')
    .select('id, notification_id, channel, recipient_email, attempts, error_details, updated_at')
    .eq('company_id', companyId)
    .eq('status', 'failed')
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[DeliveryStats] Error fetching failures:', error);
    throw error;
  }

  return (data || []).map(row => ({
    id: row.id,
    notification_id: row.notification_id,
    channel: row.channel,
    recipient_email: row.recipient_email,
    attempts: row.attempts,
    error_details: row.error_details as Record<string, unknown> | null,
    updated_at: row.updated_at,
  }));
}

/**
 * Get notification statistics for the company
 */
export async function getNotificationStats(companyId: string): Promise<NotificationStats> {
  const { data, error } = await supabase
    .from('notifications')
    .select('type_key, read_at')
    .eq('company_id', companyId);

  if (error) {
    console.error('[NotificationStats] Error fetching stats:', error);
    throw error;
  }

  const stats: NotificationStats = {
    total: data?.length || 0,
    unread: 0,
    read: 0,
    byType: {},
  };

  for (const row of data || []) {
    if (row.read_at) {
      stats.read++;
    } else {
      stats.unread++;
    }
    
    stats.byType[row.type_key] = (stats.byType[row.type_key] || 0) + 1;
  }

  return stats;
}

/**
 * Retry a failed delivery
 */
export async function retryFailedDelivery(deliveryId: string): Promise<boolean> {
  const { error } = await supabase
    .from('notification_delivery_queue')
    .update({
      status: 'pending',
      next_retry_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', deliveryId)
    .eq('status', 'failed');

  if (error) {
    console.error('[DeliveryStats] Error retrying delivery:', error);
    return false;
  }

  return true;
}
